import { useCallback, useEffect, useState } from "react";

const POSITION_KEY = "archie_bubble_position";
const SIZE_KEY = "archie_window_size";

const FAB_SIZE = 56; // MUI default Fab size
const GAP = 8; // gap between bubble and window

const DEFAULTS = {
  bottom: 24,
  right: 24,
  width: 400,
  height: 500,
};

const MIN_WIDTH = 320;
const MIN_HEIGHT = 400;
const MAX_VW_RATIO = 0.8;
const MAX_VH_RATIO = 0.8;

interface Position {
  bottom: number;
  right: number;
}

interface Size {
  width: number;
  height: number;
}

interface ArchieLayout {
  bubblePosition: Position;
  windowPosition: Position;
  windowSize: Size;
  handleBubbleDrag: (bottom: number, right: number) => void;
  handleResize: (width: number, height: number) => void;
}

const loadJson = <T,>(key: string): T | null => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

const useArchieLayout = (isOpen: boolean): ArchieLayout => {
  const [bubblePosition, setBubblePosition] = useState<Position>(() => {
    const stored = loadJson<Position>(POSITION_KEY);
    return stored ?? { bottom: DEFAULTS.bottom, right: DEFAULTS.right };
  });

  const [windowSize, setWindowSize] = useState<Size>(() => {
    const stored = loadJson<Size>(SIZE_KEY);
    return stored ?? { width: DEFAULTS.width, height: DEFAULTS.height };
  });

  // Persist position
  useEffect(() => {
    sessionStorage.setItem(POSITION_KEY, JSON.stringify(bubblePosition));
  }, [bubblePosition]);

  // Persist size
  useEffect(() => {
    sessionStorage.setItem(SIZE_KEY, JSON.stringify(windowSize));
  }, [windowSize]);

  const handleBubbleDrag = useCallback(
    (bottom: number, right: number) => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      // Basic bounds: keep the bubble itself on screen
      let maxBottom = vh - FAB_SIZE;
      let maxRight = vw - FAB_SIZE;

      if (isOpen) {
        // Tighter bounds: keep the chat window on screen too
        // Window top edge: bottom + FAB_SIZE + GAP + height <= vh
        maxBottom = vh - FAB_SIZE - GAP - windowSize.height;
        // Window left edge: right + width <= vw
        maxRight = vw - windowSize.width;
      }

      setBubblePosition({
        bottom: Math.max(0, Math.min(maxBottom, bottom)),
        right: Math.max(0, Math.min(maxRight, right)),
      });
    },
    [isOpen, windowSize]
  );

  const handleResize = useCallback((width: number, height: number) => {
    const maxW = window.innerWidth * MAX_VW_RATIO;
    const maxH = window.innerHeight * MAX_VH_RATIO;
    setWindowSize({
      width: Math.max(MIN_WIDTH, Math.min(width, maxW)),
      height: Math.max(MIN_HEIGHT, Math.min(height, maxH)),
    });
  }, []);

  // Window is positioned above the bubble
  const windowPosition: Position = {
    bottom: bubblePosition.bottom + FAB_SIZE + GAP,
    right: bubblePosition.right,
  };

  return {
    bubblePosition,
    windowPosition,
    windowSize,
    handleBubbleDrag,
    handleResize,
  };
};

export default useArchieLayout;
